import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Mic, Upload, Zap, Shield, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/ai-voice-cloning-phone-agent.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const AIVoiceCloningPhoneAgent = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.aiVoiceCloningPhoneAgent.metaTitle')}</title>
        <meta name="description" content={t('blog.aiVoiceCloningPhoneAgent.metaDescription')} />
        <meta name="keywords" content="AI voice cloning, clone your voice AI, custom voice AI phone agent, voice cloning phone calls, personalized AI voice assistant, Call24x7.AI voice cloning" />
        <link rel="canonical" href="https://call24x7.ai/blog/ai-voice-cloning-phone-agent" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/ai-voice-cloning-phone-agent" />
        <meta property="og:title" content={t('blog.aiVoiceCloningPhoneAgent.title')} />
        <meta property="og:description" content={t('blog.aiVoiceCloningPhoneAgent.metaDescription')} />
        <meta property="og:site_name" content="Call24x7.AI" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.aiVoiceCloningPhoneAgent.title'),
            "description": t('blog.aiVoiceCloningPhoneAgent.metaDescription'),
            "author": { "@type": "Organization", "name": "Call24x7.AI" },
            "publisher": { "@type": "Organization", "name": "Call24x7.AI", "url": "https://call24x7.ai" },
            "datePublished": "2025-12-21",
            "dateModified": "2025-12-21"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.aiVoiceCloningPhoneAgent.title')}
        category={t('blog.categories.voiceCloning')}
        date="December 21, 2025"
        readTime={`7 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI voice cloning for phone agents"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.aiVoiceCloningPhoneAgent.intro')}
        </p>

        <div className="grid md:grid-cols-3 gap-4 my-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Clock className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">30s</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiVoiceCloningPhoneAgent.stat1')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Zap className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">Instant</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiVoiceCloningPhoneAgent.stat2')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Shield className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">100%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiVoiceCloningPhoneAgent.stat3')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceCloningPhoneAgent.whatIsTitle')}</h2>
        <p>{t('blog.aiVoiceCloningPhoneAgent.whatIsDesc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceCloningPhoneAgent.howItWorksTitle')}</h2>
        <p className="mb-4">{t('blog.aiVoiceCloningPhoneAgent.howItWorksIntro')}</p>
        
        <div className="grid md:grid-cols-2 gap-6 my-6">
          <Card className="border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mic className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-bold">{t('blog.aiVoiceCloningPhoneAgent.option1Title')}</h3>
              </div>
              <p className="text-muted-foreground">{t('blog.aiVoiceCloningPhoneAgent.option1Desc')}</p>
            </CardContent>
          </Card>
          <Card className="border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-bold">{t('blog.aiVoiceCloningPhoneAgent.option2Title')}</h3>
              </div>
              <p className="text-muted-foreground">{t('blog.aiVoiceCloningPhoneAgent.option2Desc')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceCloningPhoneAgent.benefitsTitle')}</h2>
        <ul className="space-y-3 my-4">
          {[
            t('blog.aiVoiceCloningPhoneAgent.benefit1'),
            t('blog.aiVoiceCloningPhoneAgent.benefit2'),
            t('blog.aiVoiceCloningPhoneAgent.benefit3'),
            t('blog.aiVoiceCloningPhoneAgent.benefit4'),
            t('blog.aiVoiceCloningPhoneAgent.benefit5')
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceCloningPhoneAgent.useCasesTitle')}</h2>
        <p>{t('blog.aiVoiceCloningPhoneAgent.useCasesIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li><strong>{t('blog.aiVoiceCloningPhoneAgent.useCase1Title')}:</strong> {t('blog.aiVoiceCloningPhoneAgent.useCase1Desc')}</li>
          <li><strong>{t('blog.aiVoiceCloningPhoneAgent.useCase2Title')}:</strong> {t('blog.aiVoiceCloningPhoneAgent.useCase2Desc')}</li>
          <li><strong>{t('blog.aiVoiceCloningPhoneAgent.useCase3Title')}:</strong> {t('blog.aiVoiceCloningPhoneAgent.useCase3Desc')}</li>
          <li><strong>{t('blog.aiVoiceCloningPhoneAgent.useCase4Title')}:</strong> {t('blog.aiVoiceCloningPhoneAgent.useCase4Desc')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceCloningPhoneAgent.stepsTitle')}</h2>
        <ol className="list-decimal pl-6 my-4 space-y-3">
          <li><strong>{t('blog.aiVoiceCloningPhoneAgent.step1Title')}:</strong> {t('blog.aiVoiceCloningPhoneAgent.step1Desc')}</li>
          <li><strong>{t('blog.aiVoiceCloningPhoneAgent.step2Title')}:</strong> {t('blog.aiVoiceCloningPhoneAgent.step2Desc')}</li>
          <li><strong>{t('blog.aiVoiceCloningPhoneAgent.step3Title')}:</strong> {t('blog.aiVoiceCloningPhoneAgent.step3Desc')}</li>
          <li><strong>{t('blog.aiVoiceCloningPhoneAgent.step4Title')}:</strong> {t('blog.aiVoiceCloningPhoneAgent.step4Desc')}</li>
        </ol>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.aiVoiceCloningPhoneAgent.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.aiVoiceCloningPhoneAgent.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.aiVoiceCloningPhoneAgent.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default AIVoiceCloningPhoneAgent;
